import type { QuerySort } from '~~/shared/types/rstore';
import type { PrimitiveType } from '#layers/data-model/public';
import { Temporal } from 'temporal-polyfill';
import { compareNil, compareNumericOrNull, comparePlain, compareTemporal, compareValues } from '#layers/base/public';
import { getTypename, isPrimitiveTypeNumber } from '#layers/data-model/public';

const TEMPORAL_TYPE_BY_NAME = {
	date: Temporal.PlainDate,
	time: Temporal.PlainTime,
	dateTime: Temporal.PlainDateTime,
	dateTimeWithTimezone: Temporal.ZonedDateTime,
} as const;

type Comparator = (a: unknown, b: unknown) => number;

function resolveComparator(type: PrimitiveType | undefined): Comparator | undefined {
	if (!type) return undefined;

	const typeName = getTypename(type);
	const temporalType = TEMPORAL_TYPE_BY_NAME[typeName as keyof typeof TEMPORAL_TYPE_BY_NAME];

	if (temporalType) {
		return (a, b) => compareTemporal(temporalType, a, b);
	}

	// int64/decimal (and other numbers) arrive as strings to preserve precision;
	// compare them numerically so "100" sorts after "9", not lexically before it.
	if (isPrimitiveTypeNumber(type)) {
		return (a, b) => compareNumericOrNull(a, b) ?? comparePlain(a, b);
	}

	return comparePlain;
}

/**
 * Sorts an array of items based on the provided sort configuration.
 *
 * Null handling: nulls are placed last for ascending order, first for descending order.
 * This matches the default behavior of PostgreSQL's `NULLS LAST` for ASC and `NULLS FIRST` for DESC.
 *
 * When `fieldTypes` is provided, each sort field's comparator is resolved once
 * from its declared schema type. Fields absent from `fieldTypes` fall back to
 * the value-based path, which probes each compared value.
 *
 * @param items - The array of items to sort
 * @param sort - The sort configuration (array of field-direction pairs)
 * @param fieldTypes - Declared primitive types keyed by field name (optional)
 * @returns A new sorted array, or the original array if no sort is provided
 */
export function sortItems<T extends Record<string, unknown>>(
	items: T[],
	sort?: QuerySort,
	fieldTypes?: Record<string, PrimitiveType>,
): T[] {
	if (!sort || sort.length === 0) {
		return items;
	}

	const plan = sort
		.filter((sortItem) => Object.keys(sortItem).length > 0)
		.map((sortItem) => {
			const [fieldName] = Object.keys(sortItem) as [string];
			const [{ direction }] = Object.values(sortItem) as [{ direction: 'asc' | 'desc' }];
			const comparator = resolveComparator(fieldTypes?.[fieldName]);

			return { fieldName, direction, comparator };
		});

	return items.toSorted((a, b) => {
		for (const { fieldName, direction, comparator } of plan) {
			const aValue = a[fieldName];
			const bValue = b[fieldName];

			let cmp: number;

			if (comparator) {
				// Keep declared-type comparators aligned with compareValues' nil ordering.
				// Descending order is handled once below by flipping the final result.
				const nilResult = compareNil(aValue, bValue);

				if (nilResult !== null) {
					cmp = nilResult;
				}
				else {
					cmp = comparator(aValue, bValue);
				}
			}
			else {
				cmp = compareValues(aValue, bValue);
			}

			if (cmp !== 0) {
				return direction === 'desc' ? -cmp : cmp;
			}
		}

		return 0;
	});
}
